<!-- src/views/support-requests/SupportRequestFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F8FAFC] selection:bg-[#20FCF1]/30 font-sans">
    <div v-if="!isLoading && formConfig">
      <!-- PREMIUM SUB-HEADER -->
      <PublicSubHeader :title="formConfig.formTitle" :logo-url="clientLogoUrl" />
      
      <div class="flex-grow pt-10 px-4 pb-24">
        <div class="max-w-7xl mx-auto">
          
          <!-- BRANDED HEADER BLOCK -->
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-6 bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] animate-fadeIn">
            <div class="max-w-2xl">
              <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-tight">
                {{ formConfig.formTitle }}
              </h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Resource Logistics • Authorised for {{ formConfig.companyDisplayName }}
              </p>
            </div>
            
            <!-- LOGO CONTAINER -->
            <div v-if="formConfig.companyLogoUrl" class="flex-shrink-0 bg-white/5 p-3 rounded-[24px] border border-white/10 w-24 h-24 flex items-center justify-center overflow-hidden shadow-inner">
              <img
                :src="formConfig.companyLogoUrl"
                :alt="formConfig.companyDisplayName"
                class="max-h-full max-w-full object-contain"
              >
            </div>
          </div>

          <!-- MAIN FORM CONTENT -->
          <div class="bg-white rounded-[40px] shadow-[0_30px_60px_rgba(15,23,42,0.05)] border border-slate-100 overflow-hidden animate-fadeIn">
            <div class="p-8 sm:p-12 lg:p-16">
              
              <!-- CUSTOM INSTRUCTIONAL TEXT -->
              <div v-if="customFormText" 
                class="mb-12 p-8 bg-slate-50 rounded-[32px] border border-slate-100 text-sm text-slate-600 leading-relaxed font-medium prose-slate max-w-none" 
                v-html="renderMarkdown(customFormText)">
              </div>
              
              <form @submit.prevent="submitForm" class="space-y-12">
                <!-- GRID SYSTEM -->
                <div class="grid grid-cols-1 gap-y-10 gap-x-8 sm:grid-cols-2 lg:grid-cols-4">
                  
                  <template v-for="field in formConfig.fields" :key="field.id">
                    
                    <!-- SECTION BREAKERS -->
                    <div v-if="field.id === 'region'" class="sm:col-span-2 lg:col-span-4">
                      <h4 class="text-[11px] font-black text-slate-400 uppercase tracking-[0.4em] mb-3 px-1">Engagement Core</h4>
                      <div class="h-px bg-slate-100 w-full"></div>
                    </div>
                    
                    <div v-if="field.id === 'shiftStartDate1'" class="sm:col-span-2 lg:col-span-4 mt-4">
                      <div class="flex items-center gap-4 mb-2">
                        <span class="bg-[#0A1630] text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-md">Primary Shift</span>
                        <div class="h-px bg-slate-100 flex-grow"></div>
                      </div>
                    </div>
                    
                    <div v-if="field.id === 'shiftStartDate2' && showShift2" class="sm:col-span-2 lg:col-span-4 mt-4">
                      <div class="flex items-center gap-4 mb-2">
                        <span class="bg-[#20FCF1] text-[#0A1630] text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-md">Secondary Shift</span>
                        <div class="h-px bg-slate-100 flex-grow"></div>
                      </div>
                    </div>
                    
                    <div v-if="field.id === 'shiftStartDate3' && showShift3" class="sm:col-span-2 lg:col-span-4 mt-4">
                      <div class="flex items-center gap-4 mb-2">
                        <span class="bg-indigo-500 text-white text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-md">Tertiary Shift</span>
                        <div class="h-px bg-slate-100 flex-grow"></div>
                      </div>
                    </div>

                    <!-- GEOGRAPHIC DEPLOYMENT LOCATION (W3W) -->
                    <div v-if="field.id === 'siteAddress' && isFieldVisible(field)" class="sm:col-span-2 lg:col-span-4 p-10 bg-[#EBFDF8] rounded-[40px] border border-[#20FCF1]/20 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
                        <div class="md:col-span-2 flex items-center gap-3 mb-2">
                          <div class="bg-[#20FCF1] p-2 rounded-xl">
                            <MapPinIcon class="h-5 w-5 text-white" />
                          </div>
                          <h5 class="text-xs font-black text-[#0A1630] uppercase tracking-widest">Geographic Deployment Location</h5>
                        </div>
                        
                        <div class="flex flex-col h-full justify-end">
                          <div class="flex items-center justify-between mb-3 px-1 min-h-[32px]">
                            <label for="siteWhat3Words" class="text-[10px] font-black text-slate-500 uppercase tracking-widest">What3Words Location <span class="text-red-500 font-bold">*</span></label>
                            <a href="https://what3words.com/" target="_blank" rel="noopener noreferrer" class="opacity-80 hover:opacity-100 transition-all hover:scale-105">
                                <img src="/images/what3words-logo.png" alt="W3W" class="h-5 w-auto">
                            </a>
                          </div>
                          <input type="text" id="siteWhat3Words" v-model="formData.siteWhat3Words" placeholder="///word.word.word" 
                            class="w-full bg-white border-2 border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-[#0A1630] focus:border-[#20FCF1] outline-none transition-all shadow-sm"
                            :class="{'border-red-300 ring-red-100': locationError}">
                          <p v-if="locationError" class="text-[10px] text-red-600 mt-2 font-black uppercase tracking-widest flex items-center gap-1">
                            <ExclamationCircleIcon class="h-4 w-4" /> {{ locationError }}
                          </p>
                        </div>
                        
                        <div class="flex flex-col h-full justify-end">
                          <div class="flex items-center mb-3 px-1 min-h-[32px]">
                            <label :for="field.id" class="text-[10px] font-black text-slate-500 uppercase tracking-widest">{{ field.label }} (Optional)</label>
                          </div>
                          <input type="text" :id="field.id" v-model="formData[field.id]" 
                            class="w-full bg-white border-2 border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold text-[#0A1630] focus:border-[#20FCF1] outline-none transition-all shadow-sm">
                        </div>
                    </div>

                    <!-- STANDARD FIELDS -->
                    <div v-else-if="isFieldVisible(field)" :class="getFieldClasses(field.id)">
                      <label :for="field.id" class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-1 truncate">
                        {{ field.label }} <span v-if="field.mandatory" class="text-red-500 font-black">*</span>
                      </label>

                      <input v-if="['text', 'tel', 'number', 'datetime-local', 'email'].includes(field.type)" 
                        :type="field.type" :id="field.id" v-model="formData[field.id]" :required="isFieldRequired(field)" 
                        class="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-6 py-4 text-sm font-bold text-[#0A1630] focus:bg-white focus:border-[#20FCF1] outline-none transition-all">
                      
                      <textarea v-if="field.type === 'textarea'" :id="field.id" v-model="formData[field.id]" :required="isFieldRequired(field)" rows="4" 
                        class="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-6 py-4 text-sm font-bold text-[#0A1630] focus:bg-white focus:border-[#20FCF1] outline-none transition-all"></textarea>
                      
                      <select v-if="field.type === 'dropdown' || field.type === 'dynamic-dropdown'" :id="field.id" v-model="formData[field.id]" :required="isFieldRequired(field)" 
                        class="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-6 py-4 text-sm font-bold text-[#0A1630] focus:bg-white focus:border-[#20FCF1] outline-none transition-all appearance-none cursor-pointer">
                        <option disabled value="">Please select one</option>
                        <option v-for="opt in getOptionsForField(field)" :key="opt.value || opt" :value="opt.value || opt">{{ opt.text || opt }}</option>
                      </select>

                      <!-- DURATION WARNINGS -->
                      <div v-if="getShiftDurationWarning(field.id)" class="mt-3 text-[10px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 p-4 rounded-2xl border border-amber-100 flex items-center gap-2">
                        <ClockIcon class="h-5 w-5" /> {{ getShiftDurationWarning(field.id) }}
                      </div>
                    </div>
                  </template>
                </div>
                
                <!-- ACTION BUTTONS: DYNAMIC SHIFT ADDITION -->
                <div class="flex flex-col sm:flex-row gap-6 pt-6 no-print">
                  <button v-if="!showShift2" @click.prevent="showShift2 = true" type="button" class="flex-grow py-5 px-8 rounded-3xl border-2 border-dashed border-slate-200 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:border-[#20FCF1] hover:text-[#FD5E00] transition-all bg-white hover:bg-slate-50">
                    + Register Second Shift Period
                  </button>
                  <button v-if="showShift2 && !showShift3" @click.prevent="showShift3 = true" type="button" class="flex-grow py-5 px-8 rounded-3xl border-2 border-dashed border-slate-200 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:border-[#20FCF1] hover:text-[#FD5E00] transition-all bg-white hover:bg-slate-50">
                    + Register Third Shift Period
                  </button>
                </div>
                
                <!-- FILE ATTACHMENTS & COMMENTS -->
                <div class="grid grid-cols-1 gap-y-10 gap-x-8 sm:grid-cols-2 pt-12 border-t border-slate-100">
                    <template v-for="field in finalFields" :key="field.id">
                        <div :class="{ 'sm:col-span-2': field.gridSpan === 2 }">
                            <label :for="field.id" class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-1">
                              {{ field.label }} <span v-if="field.mandatory" class="text-red-500 font-black">*</span>
                            </label>
                            <input v-if="field.type === 'file'" type="file" :id="field.id" @change="handleFileChange" 
                              class="w-full text-xs font-bold text-slate-500 file:mr-6 file:py-4 file:px-8 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-[#0A1630] file:text-white hover:file:bg-[#20FCF1] hover:file:text-[#0A1630] transition-all cursor-pointer">
                            <textarea v-if="field.type === 'textarea'" :id="field.id" v-model="formData[field.id]" :required="isFieldRequired(field)" rows="4" 
                              class="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-6 py-4 text-sm font-bold text-[#0A1630] focus:bg-white focus:border-[#20FCF1] outline-none transition-all"></textarea>
                        </div>
                    </template>
                </div>

                <!-- SUBMIT -->
                <div class="pt-12">
                  <button type="submit" :disabled="isSubmitting" 
                    class="w-full flex justify-center py-6 px-10 bg-[#0A1630] text-white rounded-full font-black text-xs uppercase tracking-[0.3em] shadow-2xl hover:bg-[#20FCF1] hover:text-[#0A1630] hover:shadow-[0_20px_40px_rgba(56,227,176,0.3)] transition-all active:scale-[0.98] disabled:opacity-50">
                    <span v-if="isSubmitting" class="flex items-center gap-3">
                      <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Transmitting Request Registry...
                    </span>
                    <span v-else>Authorise & Submit Request</span>
                  </button>
                </div>
              </form>

              <!-- FEEDBACK -->
              <div v-if="successMessage" class="mt-10 p-8 bg-[#EBFDF8] rounded-[40px] border border-[#20FCF1]/30 text-center animate-fadeIn">
                <p class="text-[11px] font-black text-[#20FCF1] uppercase tracking-[0.4em] mb-2">Submission Successful</p>
                <p class="text-base font-bold text-[#0A1630]">{{ successMessage }}</p>
              </div>
              
              <div v-if="submissionError" class="mt-10 p-8 bg-red-50 rounded-[40px] border border-red-100 text-center animate-fadeIn">
                <p class="text-[11px] font-black text-red-600 uppercase tracking-[0.4em] mb-2">Protocol Failure</p>
                <p class="text-base font-bold text-red-900">{{ submissionError }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- STATES -->
    <div v-else-if="isLoading" class="flex flex-col items-center justify-center min-h-screen">
      <div class="w-16 h-16 border-4 border-slate-100 border-t-[#20FCF1] rounded-full animate-spin"></div>
      <p class="text-[11px] font-black text-slate-400 uppercase tracking-[0.4em] mt-8 text-center">Initialising Request Schema...</p>
    </div>

    <div v-else-if="errorMessage" class="flex flex-col items-center justify-center min-h-screen px-4 bg-slate-50">
      <div class="max-w-xl w-full bg-white p-12 rounded-[50px] shadow-2xl border border-red-50 text-center animate-fadeIn">
        <div class="bg-red-50 w-20 h-20 rounded-[30px] flex items-center justify-center mx-auto mb-8">
          <ExclamationTriangleIcon class="h-10 w-10 text-red-500" />
        </div>
        <h2 class="text-2xl font-black text-red-900 uppercase tracking-tighter mb-4">Access Restricted</h2>
        <p class="text-sm font-bold text-red-600 leading-relaxed">{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { firebaseApp } from '@/firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PublicSubHeader from '@/components/PublicSubHeader.vue';
import { marked } from 'marked';
import { 
  MapPinIcon, 
  ExclamationCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon 
} from '@heroicons/vue/24/outline';

const props = defineProps({ formSlug: { type: String, required: true } });
const functions = getFunctions(firebaseApp, 'europe-west2');
const formConfig = ref(null);
const formData = reactive({});
const clientLogoUrl = ref('');
const isLoading = ref(true);
const errorMessage = ref('');
const isSubmitting = ref(false);
const submissionError = ref('');
const successMessage = ref('');
const documentFile = ref(null);
const dynamicOptions = reactive({});
const customFormText = ref('');
const locationError = ref('');
const showShift2 = ref(false);
const showShift3 = ref(false);

const renderMarkdown = (text) => {
    try { return marked.parse(text || ''); } 
    catch (e) { return text; }
};

const initializeForm = async () => {
  try {
    isLoading.value = true;
    const res = await httpsCallable(functions, 'getPublicSupportRequestContext')({ formSlug: props.formSlug });
    const { config, logoUrl, customText, optionsMap } = res.data;
    formConfig.value = config; clientLogoUrl.value = logoUrl; customFormText.value = customText;
    Object.assign(dynamicOptions, optionsMap);
    (config.fields || []).forEach(f => formData[f.id] = f.defaultValue !== undefined ? f.defaultValue : '');
    formData.siteWhat3Words = '';
    document.title = config.formTitle || 'Engineering Request';
  } catch (err) { 
    console.error(err);
    errorMessage.value = "Validation credentials could not be retrieved."; 
  } finally { isLoading.value = false; }
};

const getFieldClasses = (id) => {
  const lowerId = id.toLowerCase();
  if (lowerId.includes('scope')) return 'sm:col-span-2 lg:col-span-4';
  if (['elr', 'tracksaffected', 'mileagefrom', 'mileageto'].includes(lowerId)) return 'lg:col-span-1 sm:col-span-1';
  return 'lg:col-span-2';
};

const isFieldVisible = (field) => {
  if (field.id.includes('2') && !showShift2.value) return false;
  if (field.id.includes('3') && !showShift3.value) return false;
  if (field.id === 'otherComments' || field.id === 'documentUpload') return false;
  if (!field.condition) return true;
  return field.condition.values.includes(formData[field.condition.field]);
};

const finalFields = computed(() => (formConfig.value?.fields || []).filter(f => f.id === 'otherComments' || f.id === 'documentUpload'));

const isFieldRequired = (field) => {
  if (field.id === 'siteAddress') return false; 
  if (field.id.includes('2') && !showShift2.value) return false;
  if (field.id.includes('3') && !showShift3.value) return false;
  return field.mandatory;
};

const getOptionsForField = (field) => {
  if (field.type === 'dynamic-dropdown' && field.optionsId) return dynamicOptions[field.optionsId] || [];
  return field.options || [];
};

const calculateDuration = (start, end) => (start && end) ? (new Date(end) - new Date(start)) / 3600000 : 0;
const shiftDurationWarnings = computed(() => ({
  shift1: calculateDuration(formData.shiftStartDate1, formData.shiftEndDate1),
  shift2: calculateDuration(formData.shiftStartDate2, formData.shiftEndDate2),
  shift3: calculateDuration(formData.shiftStartDate3, formData.shiftEndDate3),
}));

const getShiftDurationWarning = (id) => {
  const map = { shiftEndDate1: 'shift1', shiftEndDate2: 'shift2', shiftEndDate3: 'shift3' };
  return (map[id] && shiftDurationWarnings.value[map[id]] > 12) ? "Duration exceeds 12-hour threshold." : null;
};

const handleFileChange = (event) => { documentFile.value = event.target.files ? event.target.files[0] : null; };

const getBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader(); reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.onerror = e => reject(e);
});

watch(() => [formData.siteAddress, formData.siteWhat3Words], () => { if (locationError.value) locationError.value = ''; });

onMounted(initializeForm);

const submitForm = async () => {
  if (!formData.siteWhat3Words?.trim()) { locationError.value = "Coordinate Reference Required."; return; }
  isSubmitting.value = true;
  try {
    let documentBase64 = null;
    let documentName = null;
    if (documentFile.value) {
      documentBase64 = await getBase64(documentFile.value);
      documentName = documentFile.value.name;
    }

    const res = await httpsCallable(functions, 'submitGenericForm')({ 
        formIdentifier: 'supportRequest', targetCollection: 'supportRequestSubmissions', 
        submissionData: { ...formData, documentBase64, documentName, isPublicSubmission: true, configId: props.formSlug, clientUid: formConfig.value.clientId, formName: formConfig.value.formTitle, companyDisplayName: formConfig.value.companyDisplayName, companyLogoUrl: formConfig.value.companyLogoUrl } 
    });

    if (res.data.success) {
      // TRIGGER NOTIFICATION HANDSHAKE
      try {
        const notifyEng = httpsCallable(functions, 'notifyEngineeringRole');
        await notifyEng({
          title: 'New Support Request',
          description: `New request from ${formData.requestorName} (${formConfig.value.companyDisplayName})`,
          clientId: formConfig.value.clientId
        });
      } catch (notifErr) { console.warn("Notification delay in background."); }

      successMessage.value = 'Transmission finalised. Reference: ' + res.data.referenceNumber;
      (formConfig.value.fields || []).forEach(f => formData[f.id] = f.defaultValue || '');
      formData.siteWhat3Words = ''; showShift2.value = showShift3.value = false;
    }
  } catch (err) { alert("Registry synchronization failure."); } finally { isSubmitting.value = false; }
};
</script>

<style scoped>
.form-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1; }
.form-input, .form-select { @apply w-full px-6 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl text-sm font-bold text-[#0A1630] focus:bg-white focus:border-[#20FCF1] outline-none transition-all; }
.form-file-input { @apply w-full text-xs font-bold text-slate-500 file:mr-6 file:py-4 file:px-8 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-[#0A1630] file:text-white hover:file:bg-[#20FCF1] transition-all cursor-pointer; }
.animate-fadeIn { animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
.prose-slate :deep(p) { margin-bottom: 1.5em; }
</style>